import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MagnifyingGlass, CaretDown, Waves, Tag, Newspaper, Storefront, Compass, ChartLineUp, CurrencyCircleDollar, List, X, Fish } from '@phosphor-icons/react';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';

const PRIMARY_LINKS = [
  { to: '/browse', label: 'Browse' },
  { to: '/categories', label: 'Categories' },
  { to: '/marketplace', label: 'For Sale' },
  { to: '/news', label: 'News' },
];

const MORE_LINKS = [
  { to: '/tides', label: 'Tides & Fishing', icon: Waves, desc: 'Tide charts and bite times' },
  { to: '/fish-guide', label: 'Fish ID & Limits', icon: Fish, desc: 'Species chart and legal catch limits' },
  { to: '/harbours', label: 'Harbour Guide', icon: Compass, desc: 'NZ harbours, ramps and moorings' },
  { to: '/regions', label: 'By Region', icon: List, desc: 'Marine businesses in every NZ region' },
  { to: '/pricing', label: 'Pricing', icon: CurrencyCircleDollar, desc: 'Listing plans for businesses' },
];

const MOBILE_SECTIONS = [
  {
    heading: 'Directory',
    links: [
      { to: '/browse', label: 'Browse listings', icon: Storefront },
      { to: '/categories', label: 'Categories', icon: List },
      { to: '/marketplace', label: 'For Sale', icon: Tag },
      { to: '/news', label: 'Marine News', icon: Newspaper },
    ],
  },
  {
    heading: 'Tools',
    links: [
      { to: '/tides', label: 'Tides & Fishing', icon: Waves },
      { to: '/fish-guide', label: 'Fish ID & Limits', icon: Fish },
      { to: '/harbours', label: 'Harbour Guide', icon: Compass },
      { to: '/regions', label: 'By Region', icon: List },
      { to: '/pricing', label: 'Pricing', icon: ChartLineUp },
    ],
  },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [query, setQuery] = useState('');
  const [stats, setStats] = useState<{ listings: number; categories: number; regions: number } | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const moreRef = useRef<HTMLDivElement>(null);

  // Scroll-aware chrome via a sentinel (IntersectionObserver, not scroll events)
  useEffect(() => {
    const sentinel = document.createElement('div');
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:1px;pointer-events:none;';
    document.body.prepend(sentinel);
    const observer = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
      sentinel.remove();
    };
  }, []);

  useEffect(() => {
    setOpen(false);
    setMoreOpen(false);
    setSearchOpen(false);
  }, [location]);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!moreOpen) return;
    const onClick = (e: MouseEvent) => {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  // Lightweight directory-scale stats for the header (fails silent)
  useEffect(() => {
    fetch('/.netlify/functions/stats')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data && setStats(data))
      .catch(() => {});
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      navigate('/browse');
      return;
    }
    navigate(`/browse?search=${encodeURIComponent(trimmedQuery)}`);
  };

  const isActive = (path: string) => location.pathname.startsWith(path);
  const isMoreActive = MORE_LINKS.some((l) => isActive(l.to));

  const linkClass = (active: boolean) =>
    `relative font-display text-[0.9375rem] tracking-[0.01em] transition-colors py-2 ${
      active
        ? 'font-bold text-text-primary-light dark:text-text-primary'
        : 'font-medium text-text-secondary-light dark:text-text-secondary hover:text-accent'
    }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 border-b backdrop-blur-xl transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled
          ? 'border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.12)] bg-[rgba(240,244,248,0.92)] dark:bg-[rgba(6,37,54,0.97)] shadow-light-md dark:shadow-md'
          : 'border-[rgba(16,42,66,0.07)] dark:border-[rgba(214,233,247,0.1)] bg-[rgba(240,244,248,0.88)] dark:bg-[rgba(6,37,54,0.94)]'
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
        {/* Left group: brand + primary nav composed together */}
        <div className="flex items-center gap-10 min-w-0">
          <Logo />

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-7">
          {PRIMARY_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className={linkClass(isActive(link.to))}>
              {link.label}
              {isActive(link.to) && (
                <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] rounded-full bg-accent" />
              )}
            </Link>
          ))}

          {/* More dropdown */}
          <div className="relative" ref={moreRef}>
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              aria-expanded={moreOpen}
              aria-haspopup="true"
              className={`${linkClass(isMoreActive)} inline-flex items-center gap-1`}
            >
              More
              <CaretDown size={13} weight="bold" className={`transition-transform ${moreOpen ? 'rotate-180' : ''}`} />
              {isMoreActive && (
                <span className="absolute left-0 right-0 -bottom-[1px] h-[2px] rounded-full bg-accent" />
              )}
            </button>
            {moreOpen && (
              <div className="nav-menu absolute right-0 top-[calc(100%+12px)] w-[300px] bg-surface-light dark:bg-card border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.12)] rounded-xl shadow-light-lg dark:shadow-lg p-1.5 flex flex-col gap-0.5">
                {MORE_LINKS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                      isActive(item.to)
                        ? 'bg-accent-glow'
                        : 'hover:bg-hover-light dark:hover:bg-hover'
                    }`}
                  >
                    <span className="w-9 h-9 rounded-md bg-accent-glow text-accent-deep dark:text-accent-bright flex items-center justify-center flex-shrink-0">
                      <item.icon size={19} weight="regular" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-text-primary-light dark:text-text-primary">{item.label}</span>
                      <span className="block text-xs text-text-secondary-light dark:text-text-secondary mt-0.5">{item.desc}</span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          <Link
            to="/tides"
            className="hidden xl:inline-flex items-center gap-1.5 rounded-md border border-[rgba(16,42,66,0.1)] dark:border-[rgba(214,233,247,0.12)] bg-white/35 dark:bg-white/5 px-2.5 py-1.5 text-[0.68rem] font-semibold text-text-secondary-light dark:text-text-secondary hover:border-accent/35 hover:text-accent transition-colors"
            title="Tide times use LINZ tide tables and New Zealand local time"
          >
            <Waves size={14} weight="bold" />
            <span>Tides: LINZ, NZ local</span>
          </Link>

          {/* Directory scale stats - subtle, desktop only */}
          {stats && (
            <div className="hidden lg:flex items-center gap-4 pr-1 mr-1 select-none" title="MarineLink directory coverage">
              {[
                { n: stats.listings, label: 'Listings' },
                { n: stats.categories, label: 'Categories' },
                { n: stats.regions, label: 'NZ Regions' },
              ].map((s, i) => (
                <div
                  key={s.label}
                  className={`flex flex-col items-center leading-none ${
                    i > 0 ? 'pl-4 border-l border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)]' : ''
                  }`}
                >
                  <span className="font-display text-[0.95rem] font-bold text-text-primary-light dark:text-text-primary">
                    {s.n.toLocaleString('en-NZ')}
                  </span>
                  <span className="text-[0.58rem] font-semibold uppercase tracking-[0.08em] text-text-muted mt-1">
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
          )}
          {/* Desktop search */}
          <form
            onSubmit={handleSearch}
            className={`hidden md:flex items-center transition-all duration-200 ${searchOpen ? 'w-[220px]' : 'w-9'}`}
          >
            {searchOpen && (
              <input
                autoFocus
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search listings..."
                aria-label="Search listings"
                className="w-full h-9 pl-3 pr-9 rounded-md bg-surface-light dark:bg-surface border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] text-sm text-text-primary-light dark:text-text-primary placeholder:text-text-muted outline-none focus:border-accent"
              />
            )}
            <button
              type={searchOpen ? 'submit' : 'button'}
              onClick={() => !searchOpen && setSearchOpen(true)}
              aria-label={searchOpen ? 'Submit search' : 'Open search'}
              className={`flex-shrink-0 w-9 h-9 inline-flex items-center justify-center rounded-md text-text-secondary-light dark:text-text-secondary hover:text-accent hover:bg-accent-glow transition-colors ${searchOpen ? '-ml-9' : ''}`}
            >
              <MagnifyingGlass size={17} weight="bold" />
            </button>
          </form>
          <ThemeToggle />
          <Link to="/signin" className="btn btn-ghost btn-sm hidden md:inline-flex">
            Sign in
          </Link>
          <Link to="/list" className="btn btn-primary btn-sm hidden sm:inline-flex">
            List your business
          </Link>
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 -mr-1 text-text-primary-light dark:text-text-primary"
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer - full-height overlay (absolute: nav's backdrop-filter is the containing block) */}
      {open && (
        <div className="md:hidden absolute top-full left-0 right-0 z-40 h-[calc(100dvh-4rem)] bg-bg-light dark:bg-bg overflow-y-auto nav-drawer border-t border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)]">
          <div className="px-4 py-5 flex flex-col min-h-full">
            <form onSubmit={handleSearch} className="flex gap-2 mb-6">
              <label htmlFor="mobile-nav-search" className="sr-only">Search listings</label>
              <input
                id="mobile-nav-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search listings..."
                className="form-input flex-1"
              />
              <button type="submit" aria-label="Submit search" className="btn btn-primary px-4">
                <MagnifyingGlass size={17} weight="bold" />
              </button>
            </form>

            <Link
              to="/tides"
              className="mb-6 flex items-center gap-2 rounded-lg border border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.1)] bg-surface-light dark:bg-surface px-3 py-2 text-xs font-semibold text-text-secondary-light dark:text-text-secondary"
            >
              <Waves size={17} weight="bold" className="text-accent" />
              Tide times use LINZ tables and NZ local time
            </Link>

            {MOBILE_SECTIONS.map((section, sectionIndex) => (
              <div key={section.heading} className="mb-5 nav-drawer-section" style={{ animationDelay: `${sectionIndex * 60}ms` }}>
                <div className="px-3 pb-2 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
                  {section.heading}
                </div>
                <div className="flex flex-col gap-0.5">
                  {section.links.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`flex items-center gap-3.5 px-3 py-3 rounded-lg text-[0.95rem] font-medium transition-colors ${
                        isActive(link.to)
                          ? 'text-text-primary-light dark:text-text-primary bg-accent-glow'
                          : 'text-text-secondary-light dark:text-text-secondary hover:text-text-primary-light dark:hover:text-text-primary hover:bg-hover-light dark:hover:bg-hover'
                      }`}
                    >
                      <span className="w-9 h-9 rounded-md bg-accent-glow text-accent-deep dark:text-accent-bright flex items-center justify-center flex-shrink-0">
                        <link.icon size={19} weight="regular" />
                      </span>
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-auto flex gap-2 pt-5 border-t border-[rgba(16,42,66,0.09)] dark:border-[rgba(214,233,247,0.09)]">
              <Link to="/signin" className="btn btn-ghost flex-1 justify-center">
                Sign in
              </Link>
              <Link to="/list" className="btn btn-primary flex-1 justify-center">
                List your business
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
